import { Permission } from '@metron/shared';

export type CustomerDetailsTab = 'details' | 'meters' | 'properties' | 'users' | 'consumers';

export const DEFAULT_CUSTOMER_DETAILS_TAB: CustomerDetailsTab = 'details';

/** What a tab's visibility rule may consult; the shape `usePermissions` returns. */
export interface CustomerDetailsTabAccess {
  hasPermission: (permission: Permission) => boolean;
  isViewer: boolean;
}

export interface CustomerDetailsTabDef {
  id: CustomerDetailsTab;
  label: string;
  isVisible: (access: CustomerDetailsTabAccess) => boolean;
}

/**
 * The tabs of the customer details page, in display order. Details (the
 * summary cards) is open to everyone who can reach the page; each other tab
 * keeps the gate its section had before it became a tab: Meters is hidden from
 * Viewers, and the rest follow the permission their old card or page entry
 * point used.
 */
export const CUSTOMER_DETAILS_TABS: CustomerDetailsTabDef[] = [
  { id: 'details', label: 'Customer Details', isVisible: () => true },
  { id: 'meters', label: 'Meters', isVisible: ({ isViewer }) => !isViewer },
  {
    id: 'properties',
    label: 'Properties',
    isVisible: ({ hasPermission }) => hasPermission(Permission.EDIT_PROPERTIES),
  },
  {
    id: 'users',
    label: 'Plus Users',
    isVisible: ({ hasPermission }) => hasPermission(Permission.VIEW_USERS),
  },
  {
    id: 'consumers',
    label: 'MyWaterScope Users',
    isVisible: ({ hasPermission }) => hasPermission(Permission.MANAGE_END_CONSUMERS),
  },
];

export function visibleCustomerDetailsTabs(
  access: CustomerDetailsTabAccess,
): CustomerDetailsTabDef[] {
  return CUSTOMER_DETAILS_TABS.filter((tab) => tab.isVisible(access));
}

/**
 * Resolve `?tab=` against the tabs the user may see. Anything unknown or
 * hidden falls back to the default Details tab.
 */
export function parseCustomerDetailsTab(
  value: string | null,
  tabs: readonly CustomerDetailsTabDef[] = CUSTOMER_DETAILS_TABS,
): CustomerDetailsTab {
  const match = tabs.find((tab) => tab.id === value);
  return match ? match.id : DEFAULT_CUSTOMER_DETAILS_TAB;
}
